/**
 * Custom select from the mockup (`.gsel`): popover on desktop, bottom sheet on mobile (CSS in base.css).
 * Progressive enhancement over a native `<select>`, which stays the form's source of truth:
 *
 * ```html
 * <custom-select class="gsel" [data-for="select-id"]>
 *   <select name="sort_by">…</select>            ← optional when data-for points to a select elsewhere
 *   <button type="button" class="gsel-btn" aria-haspopup="listbox" aria-expanded="false">
 *     <span class="gsel-k">Trier</span><span class="gsel-v">Pertinence</span>…</button>
 *   <div class="gsel-ovl"></div>
 *   <div class="gsel-pop" role="listbox">
 *     <button type="button" role="option" class="gsel-o" data-value="manual" aria-selected="true"><span>…</span></button>…
 *   </div>
 * </custom-select>
 * ```
 *
 * Choosing an option sets the native select's value and dispatches a bubbling `change` on it. Keyboard:
 * ↑/↓/Home/End move between options, Enter/Space pick, Escape/Tab close.
 */

/**
 * Index of the option to focus after a navigation key, or `null` when the key does not move the focus.
 *
 * @param {number} current - Index of the focused option (-1 when none).
 * @param {number} length - Number of options.
 * @param {string} key - `KeyboardEvent.key`.
 * @returns {number | null}
 */
export function nextIndex(current, length, key) {
  if (length === 0) return null;
  switch (key) {
    case 'ArrowDown':
      return current < 0 ? 0 : Math.min(length - 1, current + 1);
    case 'ArrowUp':
      return current < 0 ? length - 1 : Math.max(0, current - 1);
    case 'Home':
      return 0;
    case 'End':
      return length - 1;
    default:
      return null;
  }
}

export class CustomSelect extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    // On document: the native select may live elsewhere (`data-for`) or not be parsed yet.
    document.addEventListener('change', this.onSelectChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('change', this.onSelectChange);
    document.removeEventListener('pointerdown', this.onOutside);
  }

  /** The native select this control drives. */
  get select() {
    const id = this.dataset.for;
    const el = id ? document.getElementById(id) : this.querySelector('select');
    return el instanceof HTMLSelectElement ? el : null;
  }

  get button() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('.gsel-btn'));
  }

  /** @returns {HTMLElement[]} */
  get options() {
    return [...this.querySelectorAll('[role="option"]')].filter((el) => el instanceof HTMLElement);
  }

  get isOpen() {
    return this.classList.contains('open');
  }

  open() {
    if (this.isOpen) return;
    this.classList.add('open');
    this.button?.setAttribute('aria-expanded', 'true');
    document.addEventListener('pointerdown', this.onOutside);
    const options = this.options;
    const selected = options.find((o) => o.getAttribute('aria-selected') === 'true') ?? options[0];
    selected?.focus({ preventScroll: true });
  }

  /** @param {boolean} [restoreFocus] */
  close(restoreFocus = true) {
    if (!this.isOpen) return;
    this.classList.remove('open');
    this.button?.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', this.onOutside);
    if (restoreFocus) this.button?.focus();
  }

  /**
   * Selects an option by value, mirrors it on the native select and notifies the form.
   *
   * @param {string} value
   */
  pick(value) {
    const select = this.select;
    if (select && select.value !== value) {
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.sync();
  }

  /** Reflects the native select's value on the custom control. */
  sync = () => {
    const value = this.select?.value;
    if (value == null) return;
    for (const option of this.options) {
      const on = option.dataset.value === value;
      option.setAttribute('aria-selected', String(on));
      if (on) {
        const label = this.querySelector('.gsel-v');
        if (label) label.textContent = option.textContent?.trim() ?? '';
      }
    }
  };

  /** @param {Event} event */
  onSelectChange = (event) => {
    if (event.target === this.select) this.sync();
  };

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const target = /** @type {Element} */ (event.target);
    if (!(target instanceof Element)) return;
    const option = /** @type {HTMLElement | null} */ (target.closest('[role="option"]'));
    if (option) {
      this.pick(option.dataset.value ?? '');
      this.close();
    } else if (target.closest('.gsel-btn')) {
      this.isOpen ? this.close() : this.open();
    } else if (target.closest('.gsel-ovl')) {
      this.close();
    }
  };

  /** @param {KeyboardEvent} event */
  onKeydown = (event) => {
    const target = event.target;
    if (target === this.button && !this.isOpen && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      this.open();
      return;
    }
    if (!this.isOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key === 'Tab') {
      this.close(false);
      return;
    }
    const options = this.options;
    const index = nextIndex(options.indexOf(/** @type {HTMLElement} */ (target)), options.length, event.key);
    if (index !== null) {
      event.preventDefault();
      options[index]?.focus({ preventScroll: true });
    }
  };

  /** @param {PointerEvent} event */
  onOutside = (event) => {
    if (event.target instanceof Node && !this.contains(event.target)) this.close(false);
  };
}

if (!customElements.get('custom-select')) customElements.define('custom-select', CustomSelect);
